"use client";

import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import type { SupportChatMessage } from "./api";

type Args = {
  ticketId: number | null;
  sessionToken: string | null;
};

type State = {
  socket: Socket | null;
  connected: boolean;
  agentsOnlineCount: number;
  staffTyping: { staffName: string | null; ts: number } | null;
};

export type SupportSocketHook = State & {
  sendVisitorTyping: () => void;
};

export function useSupportChatSocket({ ticketId, sessionToken }: Args): SupportSocketHook & {
  onMessage: (handler: (msg: SupportChatMessage) => void) => () => void;
} {
  const [state, setState] = useState<State>({
    socket: null,
    connected: false,
    agentsOnlineCount: 0,
    staffTyping: null,
  });
  const handlersRef = useRef<Set<(msg: SupportChatMessage) => void>>(new Set());

  useEffect(() => {
    if (!ticketId || !sessionToken) return;
    const url = process.env.NEXT_PUBLIC_STRAPI_URL;
    if (!url) return;

    const socket: Socket = io(`${url.replace(/\/$/, "")}/support-chat`, {
      auth: { sessionToken, ticketId },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });

    socket.on("connect", () => {
      setState((s) => ({ ...s, socket, connected: true }));
      socket.emit("ticket:subscribe", { ticketId });
    });

    socket.on("disconnect", () => {
      setState((s) => ({ ...s, connected: false }));
    });

    socket.on("agents:online-count", (payload: { count: number }) => {
      setState((s) => ({ ...s, agentsOnlineCount: payload?.count ?? 0 }));
    });

    socket.on(
      "message:new",
      (payload: { ticketId: number; message: SupportChatMessage }) => {
        if (payload?.ticketId !== ticketId) return;
        handlersRef.current.forEach((fn) => fn(payload.message));
      }
    );

    socket.on(
      "staff:typing",
      (payload: { ticketId: number; staffName?: string }) => {
        if (payload?.ticketId !== ticketId) return;
        setState((s) => ({
          ...s,
          staffTyping: { staffName: payload.staffName || null, ts: Date.now() },
        }));
        setTimeout(() => {
          setState((s) =>
            s.staffTyping && Date.now() - s.staffTyping.ts > 2500
              ? { ...s, staffTyping: null }
              : s
          );
        }, 2700);
      }
    );

    return () => {
      socket.disconnect();
    };
  }, [ticketId, sessionToken]);

  function sendVisitorTyping() {
    if (state.socket && state.connected && ticketId) {
      state.socket.emit("visitor:typing", { ticketId });
    }
  }

  function onMessage(handler: (msg: SupportChatMessage) => void) {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }

  return { ...state, sendVisitorTyping, onMessage };
}
